import { useState, useRef, useEffect, useMemo } from 'react';
import Icon from './Icon';
import { normName } from '../utils/normName';

// Lightweight searchable single-select combobox — no external dependency.
//
// Performance: the option lists here (doc types, statuses, suppliers) are small
// and already in memory, so we filter on EVERY keystroke — client-side
// `includes()` over a few hundred/thousand strings is sub-millisecond, and a
// debounce or min-char gate would only add latency and block 1-char queries.
// The real cost is DOM nodes, so we cap the rendered matches at MAX_RENDER and
// show a "keep typing" hint. (If a list ever reached tens of thousands, the
// right move would be server-side search + min-char + debounce — not here.)
//
// Optional `onCreate(query)`: when the typed text has no exact match, a "create"
// row appears at the end of the list, folding "add new" into the dropdown
// instead of a separate button. RTL, keyboard (arrows/Enter/Esc), click-outside.
const MAX_RENDER = 50;

// Closed, this control is deliberately indistinguishable from a native <select>:
// same box, same chevron. That is fine — until the list is long enough that
// scrolling it is the wrong move, and the user has no reason to suspect they
// could have typed. Opening therefore has to LOOK like a search box, or the
// feature stays invisible to everyone who doesn't try it by accident.
const SEARCH_HINT = 'הקלד לחיפוש…';

export default function SearchableSelect({
  value,
  onChange,
  options,            // [{ value, label }]
  placeholder = 'בחר…',
  onCreate,           // async (query) => void — omit for no create row
  onQueryChange,      // optional mirror for callers whose adjacent controls depend on the typed name
  createLabel,        // (query) => string
  initialQuery = '',  // pre-seed the search text (e.g. an OCR-extracted name to create)
  id,
  ariaLabel,
  // Show the magnifier even when CLOSED. For a control whose whole job is
  // "find the one you want" — rather than "pick from these few" — a chevron
  // makes it look like the short list it is not.
  searchAffordance = false,
  // Lets a caller put focus here from somewhere else on the page, so a nearby
  // "and 8 more" can lead into this box instead of merely naming the gap.
  inputRef: externalInputRef,
  // Inert while this ONE row's change is in flight. Gating the whole panel
  // instead would freeze a list someone is working down; gating the row keeps
  // the rest readable and scrollable.
  disabled = false,
  // Scroll the page just enough to show the whole list when it opens. Opt-in:
  // for a control low on the screen, not one the user is already looking around.
  revealMenu = false,
}) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState(initialQuery || '');
  const [active, setActive] = useState(0);
  const rootRef  = useRef(null);
  const menuRef  = useRef(null);
  const ownInputRef = useRef(null);
  const inputRef = externalInputRef || ownInputRef;
  const menuId = `${id || ariaLabel || 'ss'}-menu`;

  // Re-seed the search text when the caller's initialQuery changes — e.g. the
  // OCR-extracted supplier name arrives after this combobox has already mounted.
  // Fires only on initialQuery changes (not per keystroke), so it never clobbers
  // what the user is actively typing.
  useEffect(() => { setQuery(initialQuery || ''); }, [initialQuery]);

  const selected = options.find(o => String(o.value) === String(value));
  // Matching ignores case, surrounding/doubled spaces and quote style — the
  // server's own rule for "the same supplier name" (utils/normName.js). Typing
  // "תנובה בע״מ" therefore finds "תנובה בע"מ", and counts as an exact match, so
  // the create row is not offered for a name that already exists.
  const q = normName(query);

  // The selected option's swatch, shown in the CLOSED box. An <input> cannot
  // contain an element, so it is an adornment at the inline-start with the
  // input padded to clear it. Only while closed: once open the box is a search
  // field and its text no longer refers to the selection.
  const valueDot = !open && selected?.color ? selected.color : null;

  const allMatches = useMemo(
    () => (q ? options.filter(o => normName(o.label).includes(q)) : options),
    [q, options],
  );
  const filtered  = allMatches.slice(0, MAX_RENDER);
  const truncated = allMatches.length > MAX_RENDER;

  const exactMatch = q.length > 0 && options.some(o => normName(o.label) === q);
  const showCreate = !!onCreate && q.length > 0 && !exactMatch;

  // Rows = filtered options, plus an optional trailing "create" row.
  const rows = showCreate ? [...filtered, { __create: true }] : filtered;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && revealMenu) menuRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open, revealMenu]);

  const close    = () => { setOpen(false); setQuery(initialQuery || ''); setActive(0); };
  const openList = () => { setOpen(true); setActive(0); };

  const choose = (row) => {
    if (row.__create) { onCreate(query.trim()); close(); return; }
    onChange(row.value);
    close();
  };

  const onKeyDown = (e) => {
    // preventDefault on Enter so this never submits an ancestor <form>.
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) { openList(); return; }
      if (rows[active]) choose(rows[active]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive(a => Math.min(rows.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(0, a - 1));
    } else if (e.key === 'Escape') {
      close();
      inputRef.current?.blur();
    }
  };

  return (
    <div className="searchable-select" ref={rootRef}>
      <input
        id={id}
        ref={inputRef}
        // When closed with nothing selected, fall back to the seeded query (an
        // OCR-extracted name to create) so it's visible without opening — muted
        // via `unconfirmed` so it doesn't read as a real selection.
        className={`form-control${!open && !selected && query ? ' searchable-select-unconfirmed' : ''}${valueDot ? ' has-dot' : ''}`}
        role="combobox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        autoComplete="off"
        disabled={disabled}
        value={open ? query : (selected?.label ?? query)}
        // Open, the placeholder must invite typing. It previously repeated the
        // selected label, so clicking a supplier filter blanked the input and
        // then re-drew the same words — no visible change, no cue to type.
        placeholder={open ? SEARCH_HINT : (selected ? selected.label : placeholder)}
        onChange={e => {
          setQuery(e.target.value);
          onQueryChange?.(e.target.value);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onFocus={openList}
        // Reopen on click. After a selection the option's mouseDown preventDefault
        // keeps focus ON the input, so `onFocus` never fires again on the next
        // click — without this, clicking the box a second time to change the
        // choice did nothing. Open-only (never toggles closed) so clicking inside
        // an open search field to place the cursor can't dismiss it.
        onClick={() => { if (!open) openList(); }}
        // Close on blur (tab / click elsewhere) so two menus can't stay open.
        // Selecting an option can't blur us — option mouseDown calls
        // preventDefault — so this never fires mid-selection.
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      {valueDot && <span className="ss-dot ss-dot-value" style={{ background: valueDot }} />}
      {/* Chevron closed (reads as a select, which it is), magnifier open (reads
          as a search box, which it also is). The swap is the whole affordance. */}
      <Icon
        name={open || searchAffordance ? 'search' : 'chevron'}
        size={open || searchAffordance ? 13 : 12}
        className="searchable-select-caret"
      />
      {open && (
        <div className="searchable-select-menu" role="listbox" id={menuId} ref={menuRef}>
          {rows.length === 0 && <div className="searchable-select-empty">אין תוצאות</div>}
          {rows.map((row, i) => row.__create ? (
            <div
              key="__create"
              role="option"
              aria-selected={i === active}
              className={`searchable-select-opt create${i === active ? ' active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={e => { e.preventDefault(); choose(row); }}
            >
              <Icon name="plus" size={13} /> {createLabel ? createLabel(query.trim()) : `הוסף "${query.trim()}"`}
            </div>
          ) : (
            <div
              key={row.value}
              role="option"
              aria-selected={String(row.value) === String(value)}
              className={`searchable-select-opt${i === active ? ' active' : ''}${String(row.value) === String(value) ? ' selected' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={e => { e.preventDefault(); choose(row); }}
            >
              {/* An option may carry a colour (classifications do). It is the same
                  swatch the chips, the pie and the budget sheet use, so a row
                  in the table can be matched to them without reading names. */}
              {row.color && <span className="ss-dot" style={{ background: row.color }} />}
              {row.label}
            </div>
          ))}
          {truncated && (
            <div className="searchable-select-more">מציג {MAX_RENDER} ראשונות — המשך להקליד לצמצום</div>
          )}
        </div>
      )}
    </div>
  );
}
